import { jwtDecode, JwtPayload } from 'jwt-decode'
import type { UserClaims } from 'lib/jwtauth/claims'

// OAuth2 JWT claims (sub = user UUID, per OIDC spec)
// Also supports legacy tokens with user.* nested claims
interface TokenPayload extends JwtPayload {
	email?: string
	role?: string
	phone?: string
	user?: UserClaims & { created_at?: string }
}

export default class AccessToken {
	private accessToken: string

	public constructor(accessToken: string) {
		this.accessToken = accessToken
	}

	public getRaw(): string {
		return this.accessToken
	}

	public getEmail(): string | null {
		const claims = this.getPayloadMaybe()
		if (claims == null) return null
		return claims.email ?? claims.user?.email ?? null
	}

	public getUid(): string | null {
		const claims = this.getPayloadMaybe()
		if (claims == null) return null
		// OAuth2: sub claim; legacy fallback: user.uuid
		return claims.sub ?? claims.user?.uuid ?? null
	}

	public getExpire(): number | null {
		const claims = this.getPayloadMaybe()
		if (claims == null) return null
		return claims.exp ?? null
	}

	public getRole(): string | null {
		const claims = this.getPayloadMaybe()
		if (claims == null) return null
		return claims.role ?? claims.user?.role ?? null
	}

	public isAdmin(): boolean {
		return this.getRole() === 'admin'
	}

	public isValidToken(expireBufferInSeconds: number = 60): boolean {
		const expire = this.getExpire()
		if (expire == null) return false
		return Date.now() / 1000 < expire - expireBufferInSeconds
	}

	private getPayloadMaybe(): TokenPayload | null {
		try {
			return jwtDecode<TokenPayload>(this.accessToken)
		} catch (error) {
			console.error(error)
			return null
		}
	}
}
